import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { motion } from "framer-motion";
import { ArrowLeft, Briefcase, Plus, MapPin, Star, Wallet, CheckCircle2, AlertCircle, X } from "lucide-react";
import { STATUS_LABELS, STATUS_COLORS, CATEGORY_LABELS } from "@/lib/constants";
import { toast } from "sonner";

export default function MyPostings() {
  const [data, setData] = useState(null);
  const load = () => api.get("/my-postings").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 text-center text-muted-foreground">Indlæser...</div>;

  const completed = data.items.filter(j => j.status === "completed");
  const active = data.items.filter(j => !["completed", "cancelled"].includes(j.status));
  const cancelled = data.items.filter(j => j.status === "cancelled");

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 relative">
      <div className="absolute top-0 right-0 w-72 h-72 bg-blue-200/25 rounded-full blur-3xl -z-10 pointer-events-none animate-float" />

      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tilbage til dashboard
      </Link>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Mine opslag</h1>
          <p className="text-muted-foreground">Historik over alle jobs du har oprettet</p>
        </div>
        <Link to="/post"><Button className="gap-2 rounded-xl"><Plus className="w-4 h-4" /> Opret nyt</Button></Link>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial="hidden" animate="show"
        variants={{ show: { transition: { staggerChildren: 0.07 } } }}
        className="grid sm:grid-cols-3 gap-4 mb-8"
      >
        <SummaryCard label="Total betalt" value={`${data.total_spent.toLocaleString("da-DK")} kr.`} icon={<Wallet />} color="bg-primary/10 text-primary" />
        <SummaryCard label="Afsluttede jobs" value={data.completed_count} icon={<CheckCircle2 />} color="bg-emerald-50 text-emerald-700" />
        <SummaryCard label="Åbne lige nu" value={data.open_count} icon={<AlertCircle />} color="bg-amber-50 text-amber-700" />
      </motion.div>

      {data.items.length === 0 ? (
        <Card className="p-12 text-center">
          <Briefcase className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">Ingen opslag endnu</h3>
          <p className="text-muted-foreground mb-4">Når du opretter dit første job vises det her med fuld historik.</p>
          <Link to="/post"><Button>Opret dit første job</Button></Link>
        </Card>
      ) : (
        <Tabs defaultValue="all">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="all">Alle ({data.items.length})</TabsTrigger>
            <TabsTrigger value="active">Aktive ({active.length})</TabsTrigger>
            <TabsTrigger value="completed">Afsluttede ({completed.length})</TabsTrigger>
            <TabsTrigger value="cancelled">Annullerede ({cancelled.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="all"><PostingList items={data.items} onChanged={load} /></TabsContent>
          <TabsContent value="active"><PostingList items={active} onChanged={load} /></TabsContent>
          <TabsContent value="completed"><PostingList items={completed} onChanged={load} /></TabsContent>
          <TabsContent value="cancelled"><PostingList items={cancelled} onChanged={load} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon, color }) {
  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}>
      <Card className="p-5 lift">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color} [&>svg]:w-5 [&>svg]:h-5`}>{icon}</div>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </Card>
    </motion.div>
  );
}

function PostingList({ items, onChanged }) {
  if (items.length === 0) return <Card className="p-8 text-center text-sm text-muted-foreground">Ingen i denne kategori</Card>;
  return (
    <div className="space-y-2">
      {items.map(j => <PostingRow key={j.id} job={j} onChanged={onChanged} />)}
    </div>
  );
}

function PostingRow({ job, onChanged }) {
  const cancellable = ["open", "pending_payment"].includes(job.status);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const cancelJob = async () => {
    try {
      await api.post(`/jobs/${job.id}/cancel`);
      toast.success("Job annulleret");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kunne ikke annullere");
    }
  };

  return (
    <Link to={`/jobs/${job.id}`} data-testid={`posting-row-${job.id}`}>
      <Card className="p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge variant="outline" className={`${STATUS_COLORS[job.status]} text-xs`}>{STATUS_LABELS[job.status]}</Badge>
              {job.category && <Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[job.category]}</Badge>}
              {job.tx_status === "refunded" && <Badge variant="destructive" className="text-xs">Refunderet</Badge>}
            </div>
            <p className="font-semibold truncate">{job.title}</p>
            {job.pickup_address && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {job.pickup_address}</p>
            )}
            {job.accepted_by_name && <p className="text-xs text-muted-foreground mt-0.5">Hentet af {job.accepted_by_name}</p>}

            {/* My review */}
            {job.my_review && (
              <div className="flex items-center gap-1.5 mt-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-3 h-3 ${i < job.my_review.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
                  ))}
                </div>
                {job.my_review.comment && <span className="text-xs text-muted-foreground line-clamp-1">"{job.my_review.comment}"</span>}
              </div>
            )}
            {job.status === "completed" && !job.my_review && (
              <p className="text-xs text-amber-700 mt-1.5">↻ Mangler anmeldelse</p>
            )}
            <p className="text-[11px] text-muted-foreground/70 mt-1.5">{new Date(job.created_at).toLocaleDateString("da-DK")}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xl font-bold text-primary">{job.budget} kr.</p>
            {job.amount_paid && <p className="text-[11px] text-muted-foreground">betalt {job.amount_paid} kr.</p>}
            {job.credit_used > 0 && <p className="text-[11px] text-primary">−{job.credit_used} kr. bonus</p>}
            {cancellable && (
              <>
                <Button
                  variant="outline" size="sm"
                  className="mt-2 h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive gap-1"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmOpen(true); }}
                  data-testid={`cancel-job-btn-${job.id}`}
                >
                  <X className="w-3 h-3" /> Annuller
                </Button>
                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                  <AlertDialogContent onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Annuller "{job.title}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Jobbet fjernes fra listen over åbne jobs, og chauffører kan ikke længere byde på det. Dette kan ikke fortrydes.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-dialog-dismiss">Behold job</AlertDialogCancel>
                      <AlertDialogAction onClick={cancelJob} className="bg-destructive hover:bg-destructive/90" data-testid="cancel-dialog-confirm">
                        Ja, annuller
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
}
